import { useRef, useCallback, useMemo, useEffect } from 'react';
import debounce from 'lodash.debounce';
import scroller from 'src/js/modules/scroller';

const DEBOUNCE_MS = 200;
const MIN_INVISIBLE_RATIO = 0;
const MAX_INVISIBLE_RATIO = 0.3;

const checkVisibility = (element) => {
  const visibilityRatio = scroller.helpers.getElementVisibilityRatioAlongYAxis(element);

  // We wont't scroll in case of unknown ratio to avoid unexpected behavior
  const isUnknownRatio = visibilityRatio < MIN_INVISIBLE_RATIO;
  const isVisibleRatio = visibilityRatio > MAX_INVISIBLE_RATIO;

  return isUnknownRatio || isVisibleRatio;
};

const performScroll = (element) => {
  const centerOffset = scroller.helpers.getScrollOffset(
    element,
    scroller.constants.positions.CENTER,
    false
  );

  return scroller.helpers.scrollToPosition(centerOffset);
};

const useScrollToElement = () => {
  const isScrollingRef = useRef(false);

  const debouncedScroll = useMemo(
    () =>
      debounce((element, resolve) => {
        performScroll(element)
          .then(resolve)
          .catch(resolve)
          .finally(() => {
            isScrollingRef.current = false;
          });
      }, DEBOUNCE_MS),
    []
  );

  const scrollTo = useCallback(
    (element) => {
      if (!element || isScrollingRef.current) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          if (checkVisibility(element)) {
            resolve();
            return;
          }

          isScrollingRef.current = true;
          debouncedScroll(element, resolve);
        });
      });
    },
    [debouncedScroll]
  );

  useEffect(
    () => () => {
      debouncedScroll.cancel();
      isScrollingRef.current = false;
    },
    [debouncedScroll]
  );

  return { scrollTo };
};

export default useScrollToElement;
